// SA's paragraph settings drawn (shared/slidePlace.ts): letter spacing, space before and after each paragraph, a list's
// bullet mark and number format (each level its own mark and a step smaller, as PowerPoint's outline), columns, and
// "do not autofit". As styles and data attributes on whatever holds the words (a box's, a layout field's), read by
// slides-place.css; nothing at all for words that have none of them, so a slide made before draws as it did.

import type { CSSProperties } from "react";
import { BULLET_CHARS, MAX_LEVELS, bulletAt, hasTextMore, type Numbering } from "../../../../shared/slidePlace";
import type { TextLook } from "../../../../shared/slideDesign";

/** A number format as the browser's list style: PowerPoint's "1)" and "a)" are counter styles of slides-place.css. */
const NUM_STYLE: Record<Numbering, string> = { "1.": "decimal", "1)": "sa-paren-decimal", "a.": "lower-alpha", "a)": "sa-paren-alpha", "i.": "lower-roman", "A.": "upper-alpha", "I.": "upper-roman" };

/** The styles SA's settings add, for the element that holds the words. */
export function moreStyle(look: TextLook | undefined, fit = "var(--bfit, 1)"): CSSProperties {
  if (!look || !hasTextMore(look)) return {};
  const st: Record<string, string> = {};
  if (look.letter !== undefined) st.letterSpacing = `${look.letter}em`;
  if (look.before !== undefined || look.after !== undefined) {
    st["--sa-before"] = `calc(${look.before ?? 0}px * ${fit})`;
    st["--sa-after"] = `calc(${look.after ?? 0}px * ${fit})`;
  }
  if (look.bullet) for (let i = 0; i < MAX_LEVELS; i++) st[`--sa-bu${i + 1}`] = JSON.stringify(BULLET_CHARS[bulletAt(look.bullet, i)] + " ");
  if (look.numbering) st["--sa-nu"] = NUM_STYLE[look.numbering];
  return st as CSSProperties;
}

/** The data attributes SA's settings add (slides-place.css keys its rules on them). */
export function moreAttrs(look: TextLook | undefined): Record<string, string | undefined> {
  if (!look) return {};
  const out: Record<string, string | undefined> = {};
  if (look.before !== undefined || look.after !== undefined) out["data-sa-para"] = "";
  if (look.bullet) out["data-sa-bullet"] = look.bullet;
  if (look.numbering) out["data-sa-num"] = look.numbering;
  if (look.bullet || look.numbering) out["data-sa-outline"] = "";
  if (look.cols) out["data-sa-cols"] = String(look.cols);
  if (look.fit === "none") out["data-sa-fit"] = "none";
  return out;
}

/** What a box's fit is measured again on: SA's settings change how much room the words take. */
export const moreKey = (look: TextLook) => `${look.letter ?? ""}|${look.before ?? ""}|${look.after ?? ""}|${look.bullet ?? ""}|${look.numbering ?? ""}|${look.cols ?? ""}|${look.fit ?? ""}`;
